import { PayloadAction } from '@reduxjs/toolkit';
import { CancelToken } from 'axios';
import { call, put, select } from 'redux-saga/effects';

import { $httpRootApi, getCancelRequestSource } from '@core/http-client';
import { configActions } from '@redux/reducers/config/configReducer';
import { savedViewViewingsInsightsActions } from '@redux/reducers/saved_views/insights/savedViewViewingsInsightsReducer';
import { savedViewViewingsInsightsSelectors } from '@redux/selectors/saved_views/insights/savedViewViewingsInsightsSelector';
import {
  ManageSavedViewRequestPayload,
  PinUnpinSavedViewRequestPayload,
  SavedViewState,
  UpdateSavedViewRequestPayload,
} from '@redux/types/saved-views/common/savedViewsCommonTypes';
import { showNotification } from '@shared/components/notification';
import { getDefaultError } from '@shared/utils/common';

const getViewingsInsights = () => {
  return $httpRootApi.get(`/saved-views/viewings-insights`);
};

const getViewingsInsightsById = (id: Id) => {
  return $httpRootApi.get(`/saved-views/viewings-insights/${id}`);
};

const updatePinnedViewingsInsightsSavedViews = (
  { userId, pinnedIds }: UpdateSavedViewRequestPayload,
  cancelToken: CancelToken
) => {
  return $httpRootApi.put(
    `/users/${userId}/update/pinned_views_viewing_insights`,
    {
      pinned_views_viewing_insights: pinnedIds,
    },
    { cancelToken }
  );
};

const deleteSavedView = (id: Id) => {
  return $httpRootApi.delete(`/saved-views/${id}`);
};

const editSavedView = (data: ManageSavedViewRequestPayload) => {
  return $httpRootApi.put(`/saved-views/${data.id}`, data);
};

const createSavedView = (data: ManageSavedViewRequestPayload) => {
  return $httpRootApi.post(`/saved-views/viewings-insights`, data);
};

export function* getViewingsInsightsSaga() {
  try {
    yield put(savedViewViewingsInsightsActions.setListLoading(true));

    const { data } = yield call(getViewingsInsights);
    yield put(savedViewViewingsInsightsActions.getListSuccess(data.data));
  } catch (e) {
    const errorMessage = getDefaultError(e?.message);

    showNotification(errorMessage, 'error');
    yield put(savedViewViewingsInsightsActions.getListFailure(errorMessage));
  }
}

export function* getViewingsInsightsByIdSaga(action: PayloadAction<Id>) {
  try {
    yield put(savedViewViewingsInsightsActions.setUnpinnedViewLoading(true));

    const { data } = yield call(() => getViewingsInsightsById(action.payload));
    yield put(savedViewViewingsInsightsActions.getUnpinnedViewSuccess(data));
  } catch (e) {
    yield put(savedViewViewingsInsightsActions.getUnpinnedViewFailure(getDefaultError(e?.message)));
  }
}

export function* updatePinnedViewingsInsightsSavedViewsSaga(action: PayloadAction<UpdateSavedViewRequestPayload>) {
  const source = getCancelRequestSource();

  try {
    yield put(savedViewViewingsInsightsActions.setUpdateViewsRequestLoading(true));

    const { data } = yield call(() => updatePinnedViewingsInsightsSavedViews(action.payload, source.token));

    yield put(configActions.setUser(data));
    yield put(savedViewViewingsInsightsActions.updateViewsSuccess());
  } catch (e) {
    yield put(savedViewViewingsInsightsActions.updateViewsFailure(getDefaultError(e?.message)));
  } finally {
    const { updateLoading }: SavedViewState = yield select(savedViewViewingsInsightsSelectors.getState);

    if (updateLoading) {
      source.cancel();
    }
  }
}

export function* pinViewingsInsightsSavedViewsSaga(action: PayloadAction<PinUnpinSavedViewRequestPayload>) {
  const source = getCancelRequestSource();

  try {
    yield put(savedViewViewingsInsightsActions.setUpdateViewsRequestLoading(true));

    const { data } = yield call(() => updatePinnedViewingsInsightsSavedViews(action.payload, source.token));
    yield put(configActions.setUser(data));
    yield put(
      savedViewViewingsInsightsActions.pinViewSuccess({
        view: action.payload.view,
        isActiveView: action.payload.isActiveView,
      })
    );
  } finally {
    const { updateLoading }: SavedViewState = yield select(savedViewViewingsInsightsSelectors.getState);

    if (updateLoading) {
      source.cancel();
    }
  }
}

export function* unpinViewingsInsightsSavedViewsSaga(action: PayloadAction<PinUnpinSavedViewRequestPayload>) {
  const source = getCancelRequestSource();

  try {
    yield put(savedViewViewingsInsightsActions.setUpdateViewsRequestLoading(true));

    const { data } = yield call(() => updatePinnedViewingsInsightsSavedViews(action.payload, source.token));
    yield put(configActions.setUser(data));
    yield put(
      savedViewViewingsInsightsActions.unpinViewSuccess({
        view: action.payload.view,
        isActiveView: action.payload.isActiveView,
      })
    );
  } finally {
    const { updateLoading }: SavedViewState = yield select(savedViewViewingsInsightsSelectors.getState);

    if (updateLoading) {
      source.cancel();
    }
  }
}

export function* deleteSavedViewViewingsInsightsSaga(action: PayloadAction<Id>) {
  try {
    yield call(() => deleteSavedView(action.payload));

    yield put(configActions.deleteSavedViewViewingsInsightsSuccess(action.payload));
    yield put(savedViewViewingsInsightsActions.deleteViewSuccess(action.payload));
  } catch (e) {
    showNotification(getDefaultError(e?.message), 'error');
  }
}

export function* editSavedViewViewingsInsightsSaga(action: PayloadAction<ManageSavedViewRequestPayload>) {
  try {
    const { data } = yield call(() => editSavedView(action.payload));

    yield put(configActions.editSavedViewViewingsInsightsSuccess(data));
    yield put(savedViewViewingsInsightsActions.editViewSuccess(data));
  } catch (e) {
    showNotification(
      getDefaultError('Sorry, we could not update your view. Please contact us for assistance.'),
      'warning'
    );
  }
}

export function* createSavedViewViewingsInsightsSaga(action: PayloadAction<ManageSavedViewRequestPayload>) {
  try {
    const { data } = yield call(() => createSavedView(action.payload));

    yield put(savedViewViewingsInsightsActions.createViewSuccess(data));
  } catch (e) {
    showNotification(getDefaultError(e?.message), 'error');
  }
}
